import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Chip, 
  IconButton, 
  Tooltip, 
  Typography, 
  Alert, 
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  CloudSync as CloudSyncIcon,
  CloudOff as CloudOffIcon,
  Sync as SyncIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { databaseBridge } from '../services/DatabaseBridge';
import { SyncStatus } from '../services/DatabaseBridge';

interface SyncStatusIndicatorProps {
  compact?: boolean;
  showDetails?: boolean;
}

export const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ 
  compact = false, 
  showDetails = true 
}) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isOnline: navigator.onLine,
    lastSyncTime: null,
    savedThisSession: false,
    pendingChanges: 0,
    syncErrors: [],
    isSyncing: false
  });
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  useEffect(() => {
    // Update sync status immediately
    updateSyncStatus();

    // Set up periodic updates
    const interval = setInterval(updateSyncStatus, 5000);

    // Set up online/offline listeners
    const handleOnline = () => updateSyncStatus();
    const handleOffline = () => updateSyncStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const updateSyncStatus = () => {
    setSyncStatus(databaseBridge.getSyncStatus());
  };

  const handleForceSync = async () => {
    try {
      await databaseBridge.forceSync();
      updateSyncStatus();
    } catch (error) {
      console.error('Force sync failed:', error);
    }
  };

  const handleClearErrors = () => {
    databaseBridge.clearSyncErrors();
    updateSyncStatus();
    setShowErrorDetails(false);
  };

  const handleCleanupOrphanedItems = async () => {
    try {
      await databaseBridge.cleanupOrphanedQueueItems();
      updateSyncStatus();
    } catch (error) {
      console.error('Failed to cleanup orphaned items:', error);
    }
  };

  const getStatusColor = () => {
    if (!syncStatus.isOnline) return 'error';
    if (syncStatus.syncErrors.length > 0) return 'warning';
    if (syncStatus.pendingChanges > 0) return 'info';
    return 'success';
  };

  const getStatusIcon = () => {
    if (!syncStatus.isOnline) return <CloudOffIcon />;
    if (syncStatus.isSyncing) return <CircularProgress size={16} />;
    if (syncStatus.syncErrors.length > 0) return <ErrorIcon />;
    if (syncStatus.pendingChanges > 0) return <SyncIcon />;
    return <CheckCircleIcon />;
  };

  const getStatusText = () => {
    if (!syncStatus.isOnline) return 'Offline';
    if (syncStatus.isSyncing) return 'Syncing...';
    if (syncStatus.syncErrors.length > 0) return `${syncStatus.syncErrors.length} errors`;
    if (syncStatus.pendingChanges > 0) return `${syncStatus.pendingChanges} pending`;
    return 'Synced';
  };

  const formatLastSyncTime = () => {
    if (!syncStatus.lastSyncTime) return 'Never';
    const now = Date.now();
    const diff = now - syncStatus.lastSyncTime;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (compact) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        gap={1}
        sx={{ 
          flexWrap: 'wrap',
          minHeight: 32
        }}
      >
        <Tooltip title={getStatusText()}>
          {(!syncStatus.isOnline || syncStatus.isSyncing) ? (
            <span>
              <IconButton 
                size="small" 
                color={getStatusColor() as any}
                onClick={handleForceSync}
                disabled={true}
                sx={{ flexShrink: 0 }}
              >
                {getStatusIcon()}
              </IconButton>
            </span>
          ) : (
            <IconButton 
              size="small" 
              color={getStatusColor() as any}
              onClick={handleForceSync}
              disabled={false}
              sx={{ flexShrink: 0 }}
            >
              {getStatusIcon()}
            </IconButton>
          )}
        </Tooltip>
        {syncStatus.pendingChanges > 0 && (
          <Chip 
            size="small" 
            label={syncStatus.pendingChanges} 
            color="info" 
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Main status row */}
      <Box 
        display="flex" 
        alignItems="center" 
        gap={1} 
        mb={1}
        sx={{ 
          flexWrap: 'wrap',
          minHeight: 32
        }}
      >
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            minWidth: 'fit-content',
            flexShrink: 0
          }}
        >
          Database Sync:
        </Typography>
        
        <Chip
          icon={getStatusIcon()}
          label={getStatusText()}
          color={getStatusColor() as any}
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0 }}
        />

        {syncStatus.isOnline && syncStatus.pendingChanges > 0 && (
          <Tooltip title="Force sync now">
            {syncStatus.isSyncing ? (
              <span>
                <IconButton 
                  size="small" 
                  onClick={handleForceSync}
                  disabled={true}
                  sx={{ flexShrink: 0 }}
                >
                  <CloudSyncIcon />
                </IconButton>
              </span>
            ) : (
              <IconButton 
                size="small" 
                onClick={handleForceSync}
                disabled={false}
                sx={{ flexShrink: 0 }}
              >
                <CloudSyncIcon />
              </IconButton>
            )}
          </Tooltip>
        )}

        {syncStatus.lastSyncTime && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}
          >
            Last sync: {formatLastSyncTime()}
          </Typography>
        )}
      </Box>

      {/* Error section */}
      {showDetails && syncStatus.syncErrors.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1} 
            mb={1}
            sx={{ 
              flexWrap: 'wrap',
              minHeight: 28
            }}
          >
            <WarningIcon color="warning" fontSize="small" sx={{ flexShrink: 0 }} />
            <Typography 
              variant="caption" 
              color="warning.main"
              sx={{ flexShrink: 0 }}
            >
              {syncStatus.syncErrors.length} sync error(s)
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setShowErrorDetails(!showErrorDetails)}
              sx={{ flexShrink: 0 }}
            >
              {showErrorDetails ? 'Hide' : 'Show'} Details
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleClearErrors}
              sx={{ flexShrink: 0 }}
            >
              Clear
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleCleanupOrphanedItems}
              title="Clean up orphaned sync items"
              sx={{ flexShrink: 0 }}
            >
              Cleanup
            </IconButton>
          </Box>

          <Collapse in={showErrorDetails}>
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 1,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box sx={{ width: '100%' }}>
                {syncStatus.syncErrors.map((error, index) => (
                  <Typography 
                    key={index} 
                    variant="caption"
                    sx={{
                      display: 'block',
                      wordBreak: 'break-word',
                      lineHeight: 1.4,
                      mb: index < syncStatus.syncErrors.length - 1 ? 0.5 : 0
                    }}
                  >
                    {error}
                  </Typography>
                ))}
              </Box>
            </Alert>
          </Collapse>
        </Box>
      )}

      {/* Offline message */}
      {showDetails && !syncStatus.isOnline && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 1,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography 
            variant="caption"
            sx={{
              wordBreak: 'break-word',
              lineHeight: 1.4
            }}
          >
            Changes will be queued and synced when connection is restored.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default SyncStatusIndicator; 